import React from "react";

import { FormField, FormLabel } from "@impero-com/ui";
import { Select } from "antd";
import type { DefaultOptionType } from "antd/es/select";
import { cx } from "class-variance-authority";
import type { FilterFunc } from "rc-select/es/Select";
import { RefCallBack } from "react-hook-form";

import { getParentElement } from "../../../utils/dom";
import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";

import * as theme from "./theme.css";

// antd considers null as an invalid value in options, but not undefined
export type SelectValueType = string | number | null | undefined;
export interface SelectOptionGroupType<T> {
    key: string | number;
    label: string;
    options: (DefaultOptionType & { value: T })[];
}
export type SelectOptionType<T> =
    | (DefaultOptionType & { value: T })[]
    | SelectOptionGroupType<T>[];

export interface SelectInputBaseProps<T extends SelectValueType> {
    options: SelectOptionType<T>;
    label?: string;
    disabled?: boolean;
    className?: string;
    showSearch?: boolean;
    // Note that antd default is `true`
    dropdownMatchSelectWidth?: boolean;
    filterOption?: boolean | FilterFunc<DefaultOptionType & { value: T }>;
    // The text rendered when the value is null
    placeholder?: string;
    // Gives focus to this component when mounted
    autoFocus?: boolean;
    // Should be (in) the scrollable container
    getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
    nodeAfter?: React.ReactNode;
    // optional help text for form label
    helpText?: string;
    // optional search callback
    onSearch?: (value: string) => void;
}

export interface SelectInputProps<
    T extends SelectValueType,
> extends SelectInputBaseProps<T> {
    value: T;
    error?: string;
    fieldRef?: RefCallBack;
    onBlur?: () => void;
    // Setting the value to null via onChange is not possible
    onChange: (value: Exclude<T, null>) => void;
}

/**
 * Basic form component based on ant design's Select
 */
export function SelectInput<T extends SelectValueType>({
    options,
    label,
    filterOption,
    onChange,
    placeholder,
    className,
    nodeAfter,
    helpText,
    error,
    value,
    onBlur,
    fieldRef,
    getPopupContainer = getParentElement,
    dropdownMatchSelectWidth = false,
    disabled = false,
    autoFocus = false,
    showSearch = false,
    onSearch,
}: SelectInputProps<T>) {
    return (
        <FormField
            error={error}
            className={cx(error && ERRORED_CLASSNAME)}
            lite={label === undefined}
        >
            {label && (
                <FormLabel disabled={disabled} helpText={helpText}>
                    {label}
                </FormLabel>
            )}
            <div className={theme.inputWithText}>
                <Select<T>
                    ref={fieldRef}
                    options={options}
                    onBlur={onBlur}
                    className={cx(className, theme.selectInput)}
                    popupClassName={theme.selectInputPopup}
                    value={value}
                    placeholder={placeholder}
                    onChange={(value) => onChange(value as Exclude<T, null>)}
                    disabled={disabled}
                    status={error !== undefined ? "error" : ""}
                    autoFocus={autoFocus}
                    showSearch={showSearch}
                    filterOption={filterOption as FilterFunc<DefaultOptionType>}
                    getPopupContainer={getPopupContainer}
                    dropdownMatchSelectWidth={dropdownMatchSelectWidth}
                    onSearch={onSearch}
                />
                {nodeAfter}
            </div>
        </FormField>
    );
}
